import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import {
  ContentCopy,
  Download,
  ExpandLess,
  ExpandMore,
  Publish,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { useSnackbar } from 'notistack';

import type {
  CommitStagedResourcesRequest,
  PatientResolution,
  SessionPatientMatchSchema,
} from '../store/hooks';
import {
  useBrowseStagedResourcesQuery,
  useClearImportSelectionMutation,
  useCommitStagedResourcesMutation,
  useGetClassifySummaryQuery,
  useGetStagedResourceDocumentQuery,
  useGetStagedResourceDocumentUrlQuery,
  useGetStagedResourcesSummaryQuery,
  useImportOperationTracking,
  useListAllClassifyConflictsQuery,
  useListAllSessionPatientMatchesQuery,
  useListSourceProvidersQuery,
  useListStagedResourcesQuery,
  useRequestClassifyMutation,
  useSelectImportRowsMutation,
  useSetOrgReviewMutation,
  useSetStagedResourcesProviderMappingMutation,
} from '../store/hooks';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useDebounce } from '@hooks/useDebounce';

import {
  PROVIDERS_SURFACE,
  useMigrationBrowserParams,
} from '../hooks/use-migration-browser-params';
import {
  holdFilterParam,
  isRefusedOutcome,
  useInternalHolds,
} from '../hooks/use-internal-holds';
import { useSessionProviderMapping } from '../hooks/useSessionProviderMapping';
import { useStagedResourceResolve } from '../hooks/useStagedResourceResolve';
import { ArtifactViewerModal } from './artifact-viewer-modal';
import { CommitBoard } from './commit-board';
import type { OrgReviewFilterMode, ReviewResourceRow } from './import-review-browser';
import { CommitHeldAlert } from './internal-hold-controls';
import { commitCompleteMessage } from './internal-hold-copy';
import type { CommitHeldResult, HoldFilterMode } from './internal-hold-copy';
import {
  appointmentDescriptor,
  ImportReviewBrowser,
  reconcileOrgReviewFilter,
} from './import-review-browser';
import type {
  ImportVerdict,
  ReviewDecision,
  ReviewMatchCandidate,
  ReviewMatchResolution,
  VerdictCounts,
} from './import-review-types';
import { ProviderMappingPanel } from './provider-mapping-panel';
import { SourceDocumentModal } from './source-document-modal';
import { documentKind } from '../utils/document-preview';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

/** Filename of an artifact relative to its `_artifacts/` prefix. */
const artifactName = (key: string, prefix?: string): string =>
  prefix && key.startsWith(prefix) ? key.slice(prefix.length) : key;

const STATUSES = ['staged', 'invalid', 'committed', 'skipped'] as const;
const PER_PAGE = 25;
/** Every verdict lane at zero — the browser's totals when no verdicts exist. */
const EMPTY_VERDICT_COUNTS: VerdictCounts = {
  new: 0,
  unchanged: 0,
  update: 0,
  conflict: 0,
  ambiguous: 0,
  unprocessable: 0,
};

/**
 * Short operator-facing reason for a failed RTK Query request: the HTTP status
 * (or the fetch error kind) plus the server's message when it sent one.
 */
function queryErrorReason(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as {
    status?: number | string;
    error?: string;
    data?: { message?: string; detail?: string } | string;
  };
  const parts: string[] = [];
  if (typeof e.status === 'number') parts.push(`HTTP ${e.status}`);
  else if (typeof e.status === 'string') {
    parts.push(e.status.replaceAll('_', ' ').toLowerCase());
  }
  // Structured fields only: a gateway's 502 body is an HTML page, and pasting
  // one into an alert buries the status it came with.
  const message =
    typeof e.data === 'string' ? undefined : (e.data?.message ?? e.data?.detail ?? e.error);
  if (message) parts.push(message.slice(0, 200));
  return parts.length > 0 ? parts.join(': ') : undefined;
}
// Review-browser page size. Browsing is server-driven (type filter, search,
// and paging all query the server — GDN-2244), so this only sets how many
// decrypted rows load per page; every conflict and patient match is still
// fully loaded separately, so nothing that blocks a commit is ever hidden.
const BROWSE_PAGE_SIZE = 50;
// One page big enough to hold a session's whole selection working set (a review
// batch, not the whole import) so the bar can show the total + provenance split
// without paging. `total` stays authoritative for the count even past this cap.
const SELECTION_SUMMARY_PAGE_SIZE = 500;

/** Best-effort human label for a converted payload (mirrors the BE). */
function reviewLabel(
  data: Record<string, unknown> | undefined,
  sourceKey: string,
  resourceType?: string,
): string {
  const d = data ?? {};
  const str = (k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const first = str('first_name');
  const last = str('last_name');
  if (first || last) return [first, last].filter(Boolean).join(' ');
  for (const k of ['display_name', 'name', 'title']) {
    const v = d[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  // Type-aware fallbacks for payloads with no name-ish field — a raw source
  // key tells the operator nothing (GDN-2211).
  if (resourceType === 'encounter') {
    const kind = (str('encounter_type') || 'encounter').replaceAll('_', ' ');
    const when = (str('start_time') || str('scheduled_for')).slice(0, 10);
    return [kind, when].filter(Boolean).join(' · ');
  }
  if (resourceType === 'patient_coverage' && str('payer_name')) {
    return str('payer_name');
  }
  if (resourceType === 'claim') {
    const [, claimNumber] = sourceKey.split(':', 2);
    return `Claim ${claimNumber ?? sourceKey}`;
  }
  return sourceKey;
}

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  staged: 'info',
  invalid: 'error',
  committed: 'success',
  skipped: 'warning',
};

interface StagedResourcesSectionProps {
  organizationId: string;
  sessionId: string;
  /** Offer the Commit action (the v2 Data Import batch detail enables it). */
  showCommit?: boolean;
}

/**
 * Displays the staged `import_resources` for an import session — a summary of
 * counts by resource type × status, plus a filterable, paginated table of the
 * staged resource envelopes (status, source key, validation errors, and the
 * converted resource data). The conversion that produces these runs outside
 * this repo. With `showCommit`, also offers the commit step: staged rows
 * become real records via an async operation polled to completion.
 */
export function StagedResourcesSection({
  organizationId,
  sessionId,
  showCommit = false,
}: StagedResourcesSectionProps) {
  const [page, setPage] = useState(1);
  const [resourceType, setResourceType] = useState('');
  const [status, setStatus] = useState('');
  // Two-surface page (GDN-2256): null = Overview (board hub); a type = the
  // Review surface scoped to that type, entered from a board row.
  //
  // The open surface, the verdict lane, and the browser's type filter live in
  // the URL (GDN-3875) rather than in component state, so the back button,
  // reload, and a pasted link all land on the view the operator meant. They
  // keep their old names here: every reader below is unchanged, and only the
  // few places that CHANGE them had to learn the new writers.
  const {
    openType: urlOpenType,
    lane: browseVerdict,
    typeFilter: urlTypeFilter,
    openGroup,
    closeGroup,
    setOpenSurface,
    setLane: setBrowseVerdict,
    setTypeFilter: setBrowseType,
    dropOpenType,
    dropTypeFilter,
  } = useMigrationBrowserParams();
  // Server-driven browse (GDN-2244): the type filter, search box, and paging
  // query the server, so they cover the WHOLE import — not a loaded window.
  // The type filter is `dfilter` in the URL; search and paging stay local.
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Operator match decisions, keyed by patient source key. An ambiguous patient
  // included in the commit must be resolved here before commit is allowed.
  const [resolutions, setResolutions] = useState<Map<string, PatientResolution>>(new Map());
  const [viewerKey, setViewerKey] = useState<string | null>(null);
  const [provenanceOpen, setProvenanceOpen] = useState(false);
  // Source-document preview (GDN-2214): the staged row whose document is open.
  const [docViewId, setDocViewId] = useState<string | null>(null);
  const {
    // currentData, not data: `data` is the last result for any argument, so
    // opening a second row would paint the first row's document under it until
    // the new fetch resolved.
    currentData: docContent,
    isFetching: docLoading,
    isError: docError,
  } = useGetStagedResourceDocumentQuery(
    {
      organizationId,
      sessionId,
      importResourceId: docViewId ?? '',
    },
    { skip: !docViewId },
  );
  // How to render the open document (GDN-3874). The preview response decodes
  // the object as UTF-8 with errors="replace", so a binary file arrives with
  // its bytes destroyed; deciding this from the declared type and the body is
  // what keeps a scanned PDF's object tree off the screen.
  const docRenderKind = docContent
    ? documentKind(docContent.content_type, docContent.text ?? undefined)
    : 'text';
  // The object itself, for anything the decoded body cannot represent. Fetched
  // only for those kinds, so a text preview costs nothing extra.
  const {
    currentData: docUrl,
    isFetching: docUrlLoading,
    isError: docUrlError,
  } = useGetStagedResourceDocumentUrlQuery(
    {
      organizationId,
      sessionId,
      importResourceId: docViewId ?? '',
    },
    { skip: !docViewId || docRenderKind === 'text' },
  );
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();

  const {
    data: summary,
    // The same summary, but only ever THIS session's (GDN-3875). `data` is the
    // last result for any argument, so judging a URL against it would test the
    // new session's link against the previous session's resource types for as
    // long as the refetch took.
    currentData: summaryForSession,
    isLoading: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useGetStagedResourcesSummaryQuery({ organizationId, sessionId });

  // A link can name a group this session does not have: a type the converter
  // stopped emitting, or a URL pasted from another import. Fall back to the
  // Overview rather than drawing an empty group, which reads as a load failure.
  //
  // Decided only once THIS session's summary has arrived. While it is undefined
  // the answer is unknown, not "absent", and acting on unknown would throw the
  // operator out of a group that is merely still loading. `Object.hasOwn`, not
  // `in`: the name comes from a query string, and `'constructor' in {}` is true.
  const sessionTypes = summaryForSession?.counts_by_resource_type;
  const openTypeIsUnknown =
    urlOpenType !== null &&
    urlOpenType !== PROVIDERS_SURFACE &&
    sessionTypes !== undefined &&
    !Object.hasOwn(sessionTypes, urlOpenType);
  // Resolved during render, not left to the effect. Rendering the unknown group
  // for even one commit would fire a browse for a resource type that does not
  // exist and flash the group surface before bouncing out of it.
  const openType = openTypeIsUnknown ? null : urlOpenType;
  // The URL is then tidied to match, replacing rather than pushing: going back
  // should not return to a link that never worked.
  useEffect(() => {
    if (openTypeIsUnknown) dropOpenType();
  }, [openTypeIsUnknown, dropOpenType]);

  // A resource type's Review surface is open (the providers surface is its own
  // thing). Declared up here because the list and browse queries below scope
  // to it (GDN-3851).
  const reviewOpen = openType !== null && openType !== PROVIDERS_SURFACE;
  // A link can name a type filter the session does not have, exactly as it can
  // name a missing group, and an unhonourable filter returns an empty page from
  // a control the operator cannot see: the type select only renders inside an
  // open group. Judged against the same loaded summary, resolved in the same
  // render, and dropped from the URL the same way.
  const typeFilterIsUnknown =
    urlTypeFilter !== 'all' &&
    sessionTypes !== undefined &&
    !Object.hasOwn(sessionTypes, urlTypeFilter);
  const browseType = typeFilterIsUnknown ? (reviewOpen ? (openType as string) : 'all') : urlTypeFilter;
  // Only when the group itself is fine. Dropping an unknown group already
  // clears the filter, and two writes in one tick would each be computed from
  // the parameters captured at render, so the second would put the first back.
  useEffect(() => {
    if (typeFilterIsUnknown && !openTypeIsUnknown) dropTypeFilter();
  }, [typeFilterIsUnknown, openTypeIsUnknown, dropTypeFilter]);
  // Paging belongs to the surface it was set on. Back and forward change the
  // open group without going through openReview or closeReview, so without this
  // the operator returns to a group on page 3 of a list that is no longer the
  // one they paged through.
  useEffect(() => {
    setBrowsePage(1);
    setPage(1);
  }, [openType]);
  // An open group's surfaces all follow `browseType`, including the fallback
  // staged list: the browser owns a type dropdown of its own, so reading the
  // list off `openType` instead would leave the two disagreeing with no way to
  // correct the list (its own type select is hidden while a group is open).
  // On the Overview the list follows the operator's filter as before.
  const listResourceType = reviewOpen ? (browseType === 'all' ? '' : browseType) : resourceType;

  // Provider mapping is persisted on the session (GDN-2382), so it survives
  // reloads and is shared with commit + MCP tooling. The hook hydrates the local
  // editing copy from the summary once, then autosaves operator edits.
  const [setProviderMappingRemote] = useSetStagedResourcesProviderMappingMutation();
  const persistProviderMapping = useCallback(
    (mapping: Record<string, string>) =>
      setProviderMappingRemote({
        sessionId,
        setProviderMappingRequest: {
          organization_id: organizationId,
          provider_mapping: mapping,
        },
      }).unwrap(),
    [setProviderMappingRemote, sessionId, organizationId],
  );
  const {
    providerMapping,
    resetProviderMapping,
    onProviderMappingChange,
    retrySave: retryProviderMappingSave,
    saveStatus: mappingSaveStatus,
    savedAt: mappingSavedAt,
    isDirty: mappingDirty,
  } = useSessionProviderMapping({
    sessionId,
    persistedMapping: summary?.provider_mapping,
    persist: persistProviderMapping,
    enabled: showCommit,
  });

  const {
    // currentData, not data (GDN-3883): the page, type and status arguments all
    // change under a living component, and `data` is the last result for ANY of
    // them — so paging forward showed the previous page's rows under the new
    // page number. The spinner below already explains the gap.
    currentData: list,
    isLoading: listLoading,
    isFetching,
    refetch: refetchList,
  } = useListStagedResourcesQuery({
    organizationId,
    sessionId,
    page,
    perPage: PER_PAGE,
    resourceType: listResourceType || undefined,
    status: status || undefined,
  });

  // Classify review (GDN-2178): a verdict-aware browser over every staged row.
  // Operator decisions for conflicting rows, keyed by import_resource_id. Sent
  // as conflict_resolutions; every in-scope conflict must be decided to commit.
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({});
  const {
    data: classifySummary,
    isLoading: classifySummaryLoading,
    isError: classifySummaryError,
    error: classifySummaryErrorDetail,
    refetch: refetchClassifySummary,
  } = useGetClassifySummaryQuery({
    organizationId,
    sessionId,
  });
  const hasClassifyVerdicts = (classifySummary?.total_records ?? 0) > 0;
  // The field-merge detail (competing + fills) carries PHI — fetch it only when
  // the session has matched-with-changes rows (conflicts or updates) to enrich.
  const hasMatchedChanges =
    (classifySummary?.totals?.conflict ?? 0) > 0 || (classifySummary?.totals?.update ?? 0) > 0;
  const {
    data: conflicts,
    isLoading: conflictsLoading,
    isError: conflictsError,
    refetch: refetchConflicts,
  } = useListAllClassifyConflictsQuery(
    // Fetch EVERY conflict in the session (paged to completion), not just the
    // first browse window. An undecided conflict is the only thing that blocks
    // a commit, so all of them must be shown and resolvable no matter how large
    // the import is; a conflict left off a page would block the commit with no
    // way to decide it. GDN-2198.
    { organizationId, sessionId },
    { skip: !hasMatchedChanges },
  );
  // Commit-aware queue (GDN-2270): scope the browse + verdict chips to a commit
  // view. Default 'pending' (not-yet-committed) so committed rows drop out of
  // the review queue while unprocessable/escalated rows stay; 'committed'
  // reviews what's done; 'all' shows everything.
  const [browseStatus, setBrowseStatus] = useState<string>('pending');
  // Verdict filter (GDN-2287): filter the browse page by classify verdict
  // BE-side, pre-pagination. The server returns only the selected verdict's
  // rows, so the chip covers the WHOLE import (every conflict/ambiguous row is
  // reachable by paging), not just whatever landed on the loaded page.
  // It is `dlane` in the URL, replacing rather than pushing: a lane is a
  // filter, and pushing one would bury the group the operator came from.
  // Shared selection working set (GDN-2297). `browseSelected` drives the
  // "Selected N" filter chip through the same server browse; `pendingSelect` is
  // an optimistic overlay (id -> desired is_selected) so a checkbox responds
  // instantly and is reconciled once the refetched row agrees.
  const [browseSelected, setBrowseSelected] = useState(false);
  const [pendingSelect, setPendingSelect] = useState<Record<string, boolean>>({});
  // Org-review flag (GDN-2300). Mirrors selection, but tri-state (GDN-2366):
  // `orgReviewFilter` drives the All / Flagged / Not-flagged segmented control
  // through the same server browse, and `pendingOrgReview` is an optimistic
  // overlay (id -> desired org_review_needed) so the flag responds instantly and
  // reconciles once the refetched row agrees.
  const [orgReviewFilter, setOrgReviewFilter] = useState<OrgReviewFilterMode>('all');
  const [pendingOrgReview, setPendingOrgReview] = useState<Record<string, boolean>>({});
  // Internal hold filter (GDN-4065): All / Held / Not held, through the same
  // server browse. Holds themselves are read from the rows, not overlaid: a
  // hold is placed through a dialog and reported per row, not toggled.
  const [holdFilter, setHoldFilter] = useState<HoldFilterMode>('all');
  // A group is entered and left with its filters clean. The verdict chip in
  // particular must not survive: it is a server filter, and carrying a stale
  // one into a session with no verdicts returns nothing (GDN-3851).
  // One action, one URL write, one history entry. Writing the surface, its
  // type filter, and the cleared lane separately would compute each from the
  // parameters captured at render, so the last would drop the other two.
  const openReview = (rt: string) => {
    openGroup(rt);
    setBrowsePage(1);
    setPage(1);
  };
  const closeReview = () => {
    closeGroup();
    setBrowsePage(1);
    setPage(1);
  };
  const [browseSearch, setBrowseSearch] = useState('');
  const [browsePage, setBrowsePage] = useState(1);
  const debouncedSearch = useDebounce(browseSearch, 400);
  // New filter or search = new result set; always start it at page 1.
  useEffect(() => {
    setBrowsePage(1);
  }, [
    browseType,
    browseStatus,
    browseVerdict,
    browseSelected,
    orgReviewFilter,
    holdFilter,
    debouncedSearch,
  ]);
  const {
    // currentData, not data (GDN-3883). Every control above this table — the
    // lane chips, the type filter, the status toggle, the paging — changes the
    // argument, and with `data` the previous selection's rows stayed under the
    // new control with their Commit and Select actions live. The actions are
    // keyed by row id so they never acted on the wrong record, but on a surface
    // whose whole job is deciding what to commit, showing the operator rows
    // that are not the ones they asked for is the defect.
    currentData: browseData,
    isFetching: browseFetching,
    isError: browseError,
    refetch: refetchBrowse,
  } = useBrowseStagedResourcesQuery(
    {
      organizationId,
      sessionId,
      page: browsePage,
      perPage: BROWSE_PAGE_SIZE,
      resourceType: browseType !== 'all' ? browseType : undefined,
      // Default 'pending' (not-yet-committed) keeps committed rows out of the
      // review queue; the toggle lets an operator review Committed or All. The
      // BE reads 'pending' as a not-committed view, not an exact status
      // (GDN-2270), so invalid/escalated rows stay in the queue.
      status: browseStatus !== 'all' ? browseStatus : undefined,
      search: debouncedSearch.trim() || undefined,
      // Verdict filtering is BE-side, pre-pagination (GDN-2287): the page is
      // already the selected verdict's rows, so the browser never slices verdict
      // client-side and every conflict/ambiguous row is reachable by paging.
      verdict: browseVerdict !== 'all' ? browseVerdict : undefined,
      // Selection filter (GDN-2297) rides the same server browse.
      selected: browseSelected ? true : undefined,
      // Org-review filter rides it too, parallel to `selected`, but tri-state
      // (GDN-2366): 'flagged' -> true, 'not_flagged' -> false, 'all' -> unset.
      orgReviewNeeded:
        orgReviewFilter === 'flagged'
          ? true
          : orgReviewFilter === 'not_flagged'
            ? false
            : undefined,
      internalHold: holdFilterParam(holdFilter),
    },
    // Without verdicts the browser has nothing to lane, so the Overview skips
    // this fetch — but an OPEN group still needs its rows (GDN-3851): the
    // fallback surface lanes ambiguous patients from the match feed.
    { skip: !hasClassifyVerdicts && !reviewOpen },
  );
  const allRowsData = browseData;
  const internalHold = useInternalHolds({
    organizationId,
    sessionId,
    rows: allRowsData?.items,
    filterMode: holdFilter,
    setFilterMode: setHoldFilter,
    // Same condition as the page browse: an open group with no verdicts still
    // shows rows, so it needs their held counts too.
    skip: !hasClassifyVerdicts && !reviewOpen,
  });

  // Verdict chips rescope to the status toggle (GDN-2270). Kept separate from
  // the session-wide `classifySummary` above, which must stay unscoped so it
  // still gates the browser and the conflict fetch across every status. When
  // the toggle is 'all' the status arg is undefined, so this dedupes to the
  // same cache entry as the session-wide summary.
  // currentData (GDN-3883): these counts are scoped to the status toggle, so
  // `data` left the previous scope's numbers on the chips while the new ones
  // loaded.
  const { currentData: statusScopedSummary } = useGetClassifySummaryQuery(
    {
      organizationId,
      sessionId,
      // browseStatus is always one of the three toggle values; narrow to the
      // generated union (the 'all' case is dropped to an unscoped fetch above).
      status: browseStatus !== 'all' ? (browseStatus as 'pending' | 'committed') : undefined,
    },
    { skip: !hasClassifyVerdicts },
  );

  // ── Shared selection working set (GDN-2297) ──────────────────────────────
  // A second browse fetches the whole selected set (any status) so the bar can
  // show the total and the agent/user provenance split without paging.
  const { data: selectionSummary } = useBrowseStagedResourcesQuery(
    {
      organizationId,
      sessionId,
      page: 1,
      perPage: SELECTION_SUMMARY_PAGE_SIZE,
      selected: true,
    },
    { skip: !hasClassifyVerdicts },
  );
  const [selectImportRows] = useSelectImportRowsMutation();
  const [clearImportSelection] = useClearImportSelectionMutation();

  // Reconcile the optimistic overlay: drop an entry once the refetched row
  // agrees, or once the row leaves this page/filter (deselecting under the
  // "Selected" filter removes it). The overlay holds until the server confirms,
  // so the checkbox never flickers back.
  useEffect(() => {
    setPendingSelect(prev => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      // No page in hand means no news, not confirmation (GDN-3883). Reconciling
      // against an absent page would drop every pending entry, including one
      // whose mutation is still in flight, and the checkbox would appear to
      // revert when the next page arrived with the server's old value.
      if (!allRowsData) return prev;
      const serverById = new Map(
        (allRowsData.items ?? []).map(it => [it.import_resource_id, !!it.is_selected]),
      );
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const id of ids) {
        const want = prev[id];
        if (serverById.has(id) && serverById.get(id) !== want) {
          next[id] = want;
        } else {
          changed = true; // server caught up, or the row left this page/filter
        }
      }
      return changed ? next : prev;
    });
  }, [allRowsData]);

  // Effective selection on the current page = server truth with the optimistic
  // overlay applied; drives the per-row checkboxes (row.id === import_resource_id).
  const selectedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const it of allRowsData?.items ?? []) {
      const effective = pendingSelect[it.import_resource_id] ?? !!it.is_selected;
      if (effective) ids.add(it.import_resource_id);
    }
    return ids;
  }, [allRowsData, pendingSelect]);

  const selectedTotal = selectionSummary?.total ?? 0;
  // Provenance split for the bar. Agent picks carry selected_by === 'agent';
  // everything else is a human ("you").
  const selectionByActor = useMemo(() => {
    let agent = 0;
    let you = 0;
    for (const r of selectionSummary?.items ?? []) {
      if (r.selected_by === 'agent') agent += 1;
      else you += 1;
    }
    const out: { label: string; count: number }[] = [];
    if (agent > 0) out.push({ label: 'agent', count: agent });
    if (you > 0) out.push({ label: 'you', count: you });
    return out;
  }, [selectionSummary]);

  // Invariant: never leave the "Selected" filter on with nothing selected.
  // The selection bar (which hosts the only control to toggle the filter off)
  // is hidden when selectedTotal is 0, so if the set empties while the filter
  // is active — Clear all, or deselecting the last selected row — drop the
  // filter here; otherwise the browse stays server-filtered to an empty set and
  // the user is stranded (bot review on GDN-2297).
  useEffect(() => {
    if (browseSelected && selectedTotal === 0) {
      setBrowseSelected(false);
    }
  }, [browseSelected, selectedTotal]);

  const handleToggleSelect = useCallback(
    (id: string, next: boolean) => {
      setPendingSelect(prev => ({ ...prev, [id]: next }));
      void selectImportRows({
        organizationId,
        sessionId,
        importResourceIds: [id],
        selected: next,
      })
        .unwrap()
        .catch(() => {
          setPendingSelect(prev => {
            const { [id]: _dropped, ...rest } = prev;
            return rest;
          });
          enqueueSnackbar('Could not update selection', { variant: 'error' });
        });
    },
    [organizationId, sessionId, selectImportRows, enqueueSnackbar],
  );

  const handleClearSelection = useCallback(() => {
    void clearImportSelection({ organizationId, sessionId })
      .unwrap()
      .then(() => setPendingSelect({}))
      .catch(() => enqueueSnackbar('Could not clear selection', { variant: 'error' }));
  }, [organizationId, sessionId, clearImportSelection, enqueueSnackbar]);

  // ── Org-review flag (GDN-2300) ───────────────────────────────────────────
  // A second browse fetches the whole flagged set (any status) so the bar can
  // show the total and the agent/user provenance split without paging. Mirrors
  // the selection summary above.
  const { data: orgReviewSummary } = useBrowseStagedResourcesQuery(
    {
      organizationId,
      sessionId,
      page: 1,
      perPage: SELECTION_SUMMARY_PAGE_SIZE,
      orgReviewNeeded: true,
    },
    { skip: !hasClassifyVerdicts },
  );
  // Parallel browse for the whole NOT-flagged set (GDN-2366) so the segmented
  // control's "Not flagged N" count matches "Flagged N" without paging. Only the
  // total is needed here (no provenance read-out), so pull a single row.
  const { data: notFlaggedSummary } = useBrowseStagedResourcesQuery(
    {
      organizationId,
      sessionId,
      page: 1,
      perPage: 1,
      orgReviewNeeded: false,
    },
    { skip: !hasClassifyVerdicts },
  );
  const notFlaggedTotal = notFlaggedSummary?.total ?? 0;
  const [setOrgReview] = useSetOrgReviewMutation();

  // Reconcile the optimistic overlay: drop an entry once the refetched row
  // agrees, or once the row leaves this page/filter (unflagging under the
  // "Flagged" filter removes it). Same reconcile as pendingSelect above.
  useEffect(() => {
    setPendingOrgReview(prev => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      // Same reason as the selection overlay above.
      if (!allRowsData) return prev;
      const serverById = new Map(
        (allRowsData.items ?? []).map(it => [it.import_resource_id, !!it.org_review_needed]),
      );
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const id of ids) {
        const want = prev[id];
        if (serverById.has(id) && serverById.get(id) !== want) {
          next[id] = want;
        } else {
          changed = true; // server caught up, or the row left this page/filter
        }
      }
      return changed ? next : prev;
    });
  }, [allRowsData]);

  // Effective flag state on the current page = server truth with the optimistic
  // overlay applied; drives the per-row flag control (row.id === import_resource_id).
  const orgReviewIds = useMemo(() => {
    const ids = new Set<string>();
    for (const it of allRowsData?.items ?? []) {
      const effective = pendingOrgReview[it.import_resource_id] ?? !!it.org_review_needed;
      if (effective) ids.add(it.import_resource_id);
    }
    return ids;
  }, [allRowsData, pendingOrgReview]);

  // Provenance for flagged rows on the loaded page, keyed by row id — the
  // literal 'agent' or a user id. Feeds the per-row "flagged by …" read-out.
  const orgReviewByById = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const it of allRowsData?.items ?? []) {
      out[it.import_resource_id] = it.org_review_by ?? null;
    }
    return out;
  }, [allRowsData]);

  const orgReviewTotal = orgReviewSummary?.total ?? 0;
  // Provenance split for the bar. Agent flags carry org_review_by === 'agent';
  // everything else is a human ("you").
  const orgReviewByActor = useMemo(() => {
    let agent = 0;
    let you = 0;
    for (const r of orgReviewSummary?.items ?? []) {
      if (r.org_review_by === 'agent') agent += 1;
      else you += 1;
    }
    const out: { label: string; count: number }[] = [];
    if (agent > 0) out.push({ label: 'agent', count: agent });
    if (you > 0) out.push({ label: 'you', count: you });
    return out;
  }, [orgReviewSummary]);

  // Invariant (GDN-2366): never strand the operator on a filter whose segment is
  // empty. 'flagged' with nothing flagged, or 'not_flagged' with nothing
  // un-flagged, falls back to 'all'; but 'not_flagged' with a full import and
  // nothing flagged yet stays put — there "Not flagged" is the whole import,
  // exactly what the filter is for before anything's been flagged. Same spirit
  // as the selection guard. See reconcileOrgReviewFilter for the rules.
  useEffect(() => {
    const next = reconcileOrgReviewFilter(orgReviewFilter, orgReviewTotal, notFlaggedTotal);
    if (next !== orgReviewFilter) setOrgReviewFilter(next);
  }, [orgReviewFilter, orgReviewTotal, notFlaggedTotal]);

  const handleToggleOrgReview = useCallback(
    (id: string, needed: boolean) => {
      setPendingOrgReview(prev => ({ ...prev, [id]: needed }));
      void setOrgReview({
        organizationId,
        sessionId,
        importResourceIds: [id],
        needed,
      })
        .unwrap()
        .then(response => {
          // A refusal is a 200 with a per-row outcome (GDN-4065): flagging a
          // held row, say, after someone else held it. Drop the optimistic
          // flag — the reconcile would otherwise keep it while the server
          // disagrees — and show the server's message, which names the row,
          // why, and the next step.
          const refusal = response.outcomes?.find(
            o => o.import_resource_id === id && isRefusedOutcome(o.outcome),
          );
          if (!refusal) return;
          setPendingOrgReview(prev => {
            const { [id]: _dropped, ...rest } = prev;
            return rest;
          });
          enqueueSnackbar(refusal.message || 'The flag was refused.', { variant: 'warning' });
        })
        .catch(() => {
          setPendingOrgReview(prev => {
            const { [id]: _dropped, ...rest } = prev;
            return rest;
          });
          enqueueSnackbar('Could not update org review', { variant: 'error' });
        });
    },
    [organizationId, sessionId, setOrgReview, enqueueSnackbar],
  );

  // Review-surface verdict chips follow the type filter (GDN-2256 review
  // feedback): scoped to one type they show THAT type's lanes, not the
  // whole session's.
  const scopedClassifyCountsNow =
    browseType === 'all'
      ? statusScopedSummary?.totals
      : statusScopedSummary?.by_type?.find(t => t.resource_type === browseType)?.counts;
  // The SHAPE of the lane row is held across a scope change, the NUMBERS are
  // not (GDN-3883). Which lanes exist barely moves between scopes, so keeping
  // them stops the chip row collapsing and rebuilding under the operator; the
  // counts are suppressed by `countsPending` instead, because a number from the
  // scope they just left is the staleness this change exists to remove.
  const lastScopedCountsRef = useRef<typeof scopedClassifyCountsNow>(undefined);
  useEffect(() => {
    if (statusScopedSummary) lastScopedCountsRef.current = scopedClassifyCountsNow;
  }, [statusScopedSummary, scopedClassifyCountsNow]);
  const scopedClassifyCounts = statusScopedSummary
    ? scopedClassifyCountsNow
    : lastScopedCountsRef.current;
  const browserTotals = {
    new: 0,
    unchanged: 0,
    update: 0,
    conflict: 0,
    ambiguous: 0,
    unprocessable: 0,
    ...(scopedClassifyCounts ?? {}),
  };
  const scopedTotalRecordsNow =
    browseType === 'all'
      ? (statusScopedSummary?.total_records ?? 0)
      : Object.values(scopedClassifyCountsNow ?? {}).reduce((acc, n) => acc + (n ?? 0), 0);
  // Held like the row totals (GDN-3883): between status scopes there is no
  // count, and rendering 0 as though it were the scope's answer is its own
  // wrong number.
  const lastScopedTotalRef = useRef(0);
  useEffect(() => {
    if (statusScopedSummary) lastScopedTotalRef.current = scopedTotalRecordsNow;
  }, [statusScopedSummary, scopedTotalRecordsNow]);
  const scopedTotalRecords = statusScopedSummary
    ? scopedTotalRecordsNow
    : lastScopedTotalRef.current;

  // Held across the gap between arguments (GDN-3883). `currentData` is
  // undefined while the next page loads, and a total of 0 unmounts the pager
  // the operator's focus is sitting on, then remounts it a moment later.
  const lastBrowseTotalRef = useRef(0);
  useEffect(() => {
    if (allRowsData) {
      lastBrowseTotalRef.current = allRowsData.total ?? allRowsData.items?.length ?? 0;
    }
  }, [allRowsData]);
  const browseTotalRows = allRowsData
    ? (allRowsData.total ?? allRowsData.items?.length ?? 0)
    : lastBrowseTotalRef.current;
  // Whole-session totals from the summary (independent of any page).
  const totalConflictCount = classifySummary?.totals?.conflict ?? 0;
  // Pathological guard: the conflict fetch pages to completion, so this is only
  // true for an absurdly large conflict set (beyond the fetch's page backstop).
  // Gate the commit if we somehow couldn't load them all rather than miss one.
  const conflictsIncomplete =
    !!conflicts && (conflicts.total ?? 0) > (conflicts.items?.length ?? 0);

  // Per-patient match outcomes (staged patients only). Feeds the resolution UI
  // in the patient picker AND the ambiguous rows in the review browser — the
  // match feed carries candidate names/dob the staged row's persisted candidate
  // ids don't. Fetched to completion (paged past the 1000 cap) so a large batch
  // never silently hides an ambiguous match; matchesIncomplete still gates as a
  // backstop for the pathological case. GDN-2198 / GDN-2212.
  const {
    data: matchData,
    isLoading: matchesLoading,
    isError: matchesError,
    refetch: refetchMatches,
  } = useListAllSessionPatientMatchesQuery({ organizationId, sessionId }, { skip: !showCommit });
  const matchByKey = useMemo(
    () =>
      new Map<string, SessionPatientMatchSchema>(
        (matchData?.items ?? []).map(m => [m.patient_key, m]),
      ),
    [matchData],
  );
  const matchesIncomplete = !!matchData && (matchData.total ?? 0) > (matchData.items?.length ?? 0);
  // Patients the commit will silently link to existing records: correct when
  // the match is right, chart-corrupting when it isn't — say so up front.
  // A held patient is not linked by any commit (GDN-4065), so it isn't counted.
  const autoLinkCount = useMemo(
    () => [...matchByKey.values()].filter(m => m.status === 'matched' && !m.internal_hold).length,
    [matchByKey],
  );
  const setResolution = useCallback(
    (key: string, resolution: PatientResolution | null) =>
      setResolutions(prev => {
        const next = new Map(prev);
        if (resolution) next.set(key, resolution);
        else next.delete(key);
        return next;
      }),
    [],
  );

  const reviewRows = useMemo<ReviewResourceRow[]>(() => {
    const items = allRowsData?.items ?? [];
    const conflictItems = conflicts?.items ?? [];
    const detailById = new Map(conflictItems.map(c => [c.import_resource_id, c]));
    const toDiff = (d: { field: string; existing?: string | null; incoming?: string | null }) => ({
      field: d.field,
      existing: d.existing ?? '',
      incoming: d.incoming ?? '',
    });
    // Candidates for an ambiguous patient row come from the match feed (which
    // carries names/dob for display); the staged row's persisted
    // match_candidates (ids + confidence only) are the fallback.
    const candidatesFor = (
      sourceKey: string,
      persisted:
        | {
            patient_id: string;
            confidence: number;
            reason: string;
            name?: string | null;
            dob?: string | null;
            identifier?: string | null;
          }[]
        | undefined,
    ): ReviewMatchCandidate[] | undefined => {
      // Carry identity structured so the browser renders name/DOB/MRN and never
      // shows a raw patient id (GDN-2344). The live match feed is preferred; the
      // persisted blob is the fallback and now also carries demographics once the
      // backend hydrates them on read.
      const fromMatch = matchByKey.get(sourceKey)?.candidates;
      if (fromMatch && fromMatch.length > 0) {
        return fromMatch.map(c => ({
          patientId: c.patient_id,
          name: c.name ?? undefined,
          dob: c.dob ?? undefined,
          identifier: c.identifier ?? undefined,
          reason: c.reason,
          confidence: c.confidence,
        }));
      }
      return persisted?.map(c => ({
        patientId: c.patient_id,
        name: c.name ?? undefined,
        dob: c.dob ?? undefined,
        identifier: c.identifier ?? undefined,
        reason: c.reason,
        confidence: c.confidence,
      }));
    };
    // Why a row is ambiguous — carried on the match feed (GDN-2244); read via
    // a cast until the generated client picks the field up.
    const basisFor = (sourceKey: string): string | undefined =>
      (matchByKey.get(sourceKey) as { match_basis?: string } | undefined)?.match_basis ?? undefined;
    // Patient labels by source key, so child rows (encounters) can say whose
    // record they are. The match feed covers every staged patient regardless
    // of which browse page is loaded; page rows fill any gap (e.g. when the
    // match feed isn't fetched because commit isn't shown).
    const patientNameByKey = new Map<string, string>();
    for (const [key, m] of matchByKey) {
      if (m.name) patientNameByKey.set(key, m.name);
    }
    for (const i of items) {
      if (i.resource_type === 'patient' && !patientNameByKey.has(i.source_key)) {
        patientNameByKey.set(i.source_key, reviewLabel(i.data, i.source_key, 'patient'));
      }
    }
    const windowedRows = items.flatMap(item => {
      const classification = (item as { classification?: string | null }).classification;
      // A session whose classify pass recorded no verdict still has a complete
      // patient-match feed, and the board's "N to decide" counts that feed's
      // ambiguous matches. Lane an unclassified patient row as ambiguous when
      // the feed says so, so the detail shows the rows the board counted. Only
      // this lane is derived; every other verdict is the classifier's alone
      // (GDN-3851).
      const ambiguousByMatch =
        item.resource_type === 'patient' &&
        !classification &&
        matchByKey.get(item.source_key)?.status === 'ambiguous';
      const verdict: ImportVerdict | undefined =
        item.status === 'invalid'
          ? 'unprocessable'
          : ((classification as ImportVerdict | undefined) ??
            (ambiguousByMatch ? 'ambiguous' : undefined));
      if (!verdict) return [];
      const detail = detailById.get(item.import_resource_id);
      let label = detail?.title ?? reviewLabel(item.data, item.source_key, item.resource_type);
      // Owning patient for a child row: prefer the server-resolved ref label
      // (present regardless of the browse page, GDN-2264), fall back to the
      // page-local patient map. When there is no patient ref at all but the
      // converter recorded an intended name, flag it as unlinked.
      const refLabels = (item as { ref_labels?: Record<string, string> }).ref_labels;
      const metadata = (item.data?.metadata as { source_patient_name?: unknown } | undefined) ?? {};
      const sourceName =
        typeof metadata.source_patient_name === 'string' ? metadata.source_patient_name : '';
      let patientLabel: string | undefined;
      let patientUnlinked = false;
      if (item.resource_type !== 'patient') {
        const resolved = refLabels?.patient ?? patientNameByKey.get(item.refs?.patient ?? '');
        if (resolved && resolved !== (item.refs?.patient ?? '')) {
          patientLabel = resolved;
        } else if (!item.refs?.patient && sourceName) {
          patientLabel = sourceName;
          patientUnlinked = true;
        }
      }
      const row: ReviewResourceRow = {
        id: item.import_resource_id,
        resourceType: item.resource_type,
        label,
        // The sublabel carries provenance (the source key). Appointments lead
        // with the patient instead, so their detail line is the appointment's
        // own type/date/duration rather than the opaque source key (GDN-2269);
        // when the label already IS the source key, print nothing.
        sublabel:
          item.resource_type === 'appointment'
            ? appointmentDescriptor(item.data)
            : label === item.source_key
              ? undefined
              : item.source_key,
        verdict,
        matchedSummary: detail?.matched_summary ?? undefined,
        competing: detail?.competing?.map(toDiff),
        autoMerge: detail?.auto_merge?.map(toDiff),
        matchCandidates:
          verdict === 'ambiguous'
            ? candidatesFor(
                item.source_key,
                (
                  item as {
                    match_candidates?: {
                      patient_id: string;
                      confidence: number;
                      reason: string;
                      name?: string | null;
                      dob?: string | null;
                      identifier?: string | null;
                    }[];
                  }
                ).match_candidates,
              )
            : undefined,
        matchBasis: verdict === 'ambiguous' ? basisFor(item.source_key) : undefined,
        data: item.data,
        patientKey: item.resource_type === 'patient' ? item.source_key : undefined,
        patientLabel,
        patientUnlinked: patientUnlinked || undefined,
        committed: item.status === 'committed' || undefined,
        // Dependency ref targets + this row's own committed id (GDN-2376): feed
        // the per-ref chips and the dependency-aware per-row commit. Carried on
        // the BrowsedStagedResource bridge until codegen picks ref_targets up.
        refTargets: item.ref_targets,
        committedResourceId: item.committed_resource_id ?? undefined,
        // Provider mapping state (GDN-2412). Status follows the LOCAL live
        // mapping (the map commit actually consumes — the BE decoration is the
        // persisted copy and lags local edits by the autosave); the BE state
        // supplies the mapped user's name + current profile when it agrees on
        // the user.
        providerMapping:
          item.resource_type === 'provider'
            ? (() => {
                const beState = item.provider_mapping_state ?? undefined;
                const localUser = providerMapping[item.source_key];
                if (!localUser) return { status: 'needs_mapping' as const };
                return beState?.user_id === localUser
                  ? beState
                  : { status: 'mapped' as const, user_id: localUser };
              })()
            : undefined,
      };
      return [row];
    });
    // Server-driven browse (GDN-2287): the page is already the BE-filtered,
    // pre-paginated result set (type + status + verdict + search), so we render
    // it as-is. No client-side merge of out-of-window conflict/ambiguous rows —
    // that "pinned rows on every page" merge is what made the same conflicts
    // appear on every page and let committed rows leak into the Pending queue.
    // Every conflict/ambiguous row is now reachable by selecting its verdict
    // chip and paging; the commit gate still counts undecided work from the full
    // conflict/match feeds below, so nothing is missed by not pinning them here.
    return windowedRows;
  }, [allRowsData, conflicts, matchByKey, providerMapping]);

  // Ambiguous patient rows carry their resolution through the same
  // `resolutions` map (keyed by patient source key) the patient picker uses —
  // one source of truth, both UIs stay in sync. The browser speaks row ids, so
  // adapt both directions. Beyond-window ambiguous rows are synthesized with
  // the source key AS the row id, so those map to themselves.
  const patientKeyByRowId = useMemo(() => {
    const map = new Map(
      (allRowsData?.items ?? [])
        .filter(i => i.resource_type === 'patient')
        .map(i => [i.import_resource_id, i.source_key]),
    );
    for (const [key, m] of matchByKey) {
      if (m.status === 'ambiguous' && !map.has(key)) map.set(key, key);
    }
    return map;
  }, [allRowsData, matchByKey]);
  const browserMatchResolutions = useMemo(() => {
    const out: Record<string, ReviewMatchResolution> = {};
    for (const [rowId, key] of patientKeyByRowId) {
      const r = resolutions.get(key);
      if (!r) continue;
      out[rowId] =
        r.action === 'link' && r.target_patient_id
          ? { action: 'link', targetPatientId: r.target_patient_id }
          : { action: 'create' };
    }
    return out;
  }, [patientKeyByRowId, resolutions]);

  const countsByType = summary?.counts_by_resource_type ?? {};
  const resourceTypes = Object.keys(countsByType).sort();
  const items = list?.items ?? [];
  const lastListTotalRef = useRef(0);
  useEffect(() => {
    if (list) lastListTotalRef.current = list.total ?? 0;
  }, [list]);
  const total = list ? (list.total ?? 0) : lastListTotalRef.current;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  // A smaller result can land under a page number that no longer exists — a
  // refresh that shrinks the set, or a filter arriving after the page moved.
  useEffect(() => {
    if (list && page > totalPages) setPage(totalPages);
  }, [list, page, totalPages]);

  const refresh = () => {
    void refetchSummary();
    void refetchList();
  };

  // Per-row "decide = commit now" (GDN-2324). A match/resolve pick commits the
  // single row immediately via the dedicated endpoints instead of staging into
  // the batch-commit maps. The generated mutations carry no cache tags, so on
  // commit we refetch the browser's data sources to drop the committed row and
  // refresh the verdict/gate counts. The whole-batch Commit is untouched — it
  // just handles the clean rows nobody resolved by hand.
  const refreshReview = useCallback(() => {
    void refetchBrowse();
    void refetchSummary();
    void refetchList();
    void refetchClassifySummary();
    void refetchConflicts();
    void refetchMatches();
  }, [
    refetchBrowse,
    refetchSummary,
    refetchList,
    refetchClassifySummary,
    refetchConflicts,
    refetchMatches,
  ]);
  const clearDecision = useCallback(
    (id: string) =>
      setDecisions(prev => {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }),
    [],
  );
  const { resolvingIds, resolveRow, matchRow } = useStagedResourceResolve({
    organizationId,
    sessionId,
    // Called by the hook after every server-state-changing outcome (a commit AND
    // a match's needs_resolution reclassify). The generated mutations carry no
    // cache tags, so nothing auto-invalidates the list/browse/conflicts queries.
    refetch: refreshReview,
    onCommitted: id => {
      // Drop the optimistic pick(s) so a committed row can't leak into the
      // batch-commit payload; the hook's refetch drops the row from the list.
      clearDecision(id);
      const key = patientKeyByRowId.get(id);
      if (key) setResolution(key, null);
    },
    onNeedsResolution: id => {
      // The chosen record has competing fields — drop the match pick so the row
      // presents to the whole-record decision buttons as a concrete conflict.
      const key = patientKeyByRowId.get(id);
      if (key) setResolution(key, null);
    },
    onError: id => {
      clearDecision(id);
      const key = patientKeyByRowId.get(id);
      if (key) setResolution(key, null);
    },
  });

  // Optimistically reflect the pick (so the row highlights while it commits),
  // then fire the per-row endpoint. A null pick just clears the local state.
  const handleDecisionChange = useCallback(
    (id: string, decision: ReviewDecision | null) => {
      if (!decision) {
        clearDecision(id);
        return;
      }
      setDecisions(prev => ({ ...prev, [id]: decision }));
      resolveRow(id, decision);
    },
    [clearDecision, resolveRow],
  );
  const handleMatchResolution = useCallback(
    (rowId: string, resolution: ReviewMatchResolution | null) => {
      const key = patientKeyByRowId.get(rowId);
      if (!key) return;
      setResolution(
        key,
        resolution == null
          ? null
          : resolution.action === 'link'
            ? { action: 'link', target_patient_id: resolution.targetPatientId }
            : { action: 'create' },
      );
      if (resolution) matchRow(rowId, resolution);
    },
    [patientKeyByRowId, setResolution, matchRow],
  );

  // The converter provenance recorded at staging time (GDN-1978): the
  // _artifacts/ prefix, the artifact object keys, and the convert.py hash.
  const artifactsPrefix = summary?.artifacts_prefix ?? undefined;
  const artifactKeys = summary?.artifact_keys ?? [];
  const converterSha256 = summary?.converter_sha256 ?? undefined;
  const hasProvenance = Boolean(artifactsPrefix || converterSha256 || artifactKeys.length);

  // If the loader stashed `_artifacts/provider_mapping.json` with the batch, read
  // it so the provider-mapping step can offer a one-click pre-fill instead of
  // resolving each provider by hand. Best-effort: any failure just hides the
  // button. Mirrors ArtifactViewerModal's content fetch.
  const mappingArtifactKey = useMemo(
    () =>
      (summary?.artifact_keys ?? []).find(
        k => artifactName(k, artifactsPrefix) === 'provider_mapping.json',
      ),
    [summary?.artifact_keys, artifactsPrefix],
  );
  const [suggestedMapping, setSuggestedMapping] = useState<Record<string, string> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!showCommit || !mappingArtifactKey) {
      setSuggestedMapping(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAuth0AccessTokenSilently();
        const params = new URLSearchParams({
          organization_id: organizationId,
          key: mappingArtifactKey,
        });
        const res = await fetch(
          `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/content?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const data = (await res.json()) as { text?: string };
        const parsed: unknown = data.text ? JSON.parse(data.text) : undefined;
        if (!cancelled && parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setSuggestedMapping(parsed as Record<string, string>);
        }
      } catch {
        // best-effort; no pre-fill button if the artifact can't be read/parsed
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCommit, mappingArtifactKey, organizationId, sessionId]);

  // Open a single artifact via a short-lived presigned URL. The backend only
  // signs keys recorded on this session, so an arbitrary key can't be fetched.
  // Fetched fresh each time (not via cached RTK query) since the URL expires.
  const openArtifact = async (key: string) => {
    try {
      const token = await getAuth0AccessTokenSilently();
      const params = new URLSearchParams({
        organization_id: organizationId,
        key,
      });
      const res = await fetch(
        `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/download?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        throw new Error(`Download failed (${res.status})`);
      }
      const data = (await res.json()) as { url: string };
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      enqueueSnackbar('Could not open artifact.', { variant: 'error' });
    }
  };

  // Type scoping now lives on the commit board (per-type Commit buttons,
  // GDN-2253); the header button always commits everything still staged.
  const selectableTypes = resourceTypes.filter(rt => (countsByType[rt]?.staged ?? 0) > 0);
  const selectedTypes = selectableTypes;
  const selectedStagedCount = selectedTypes.reduce(
    (acc, rt) => acc + (countsByType[rt]?.staged ?? 0),
    0,
  );
  const allSelected = selectableTypes.length > 0;

  // Provider mapping gate: encounters commit owned by their source provider,
  // so a required (encounter-referenced) provider must be mapped before the
  // commit can include encounters — else those rows block server-side.
  const { data: sourceProviders } = useListSourceProvidersQuery(
    { organizationId, sessionId },
    { skip: !showCommit },
  );
  const providers = sourceProviders?.items ?? [];
  const encounterSelected = selectedTypes.includes('encounter');
  // Required (encounter/note-referenced) providers still unmapped. Computed
  // unconditionally so the provider board row — and every place that reports
  // "N to map" — shows the true figure even when encounters aren't in the
  // current selection (a provider-only staged batch, or notes referencing
  // providers with no encounter type). GDN-2256.
  const unmappedRequiredProviders = providers.filter(
    p => p.required && !providerMapping[p.provider_key],
  );
  // The encounter commit hard-requires its referenced providers be mapped, so
  // that gate only bites when encounters are actually in the selection.
  const unmappedRequired = encounterSelected ? unmappedRequiredProviders : [];
  const providerGated = unmappedRequired.length > 0;

  // A failed query must read as a failure, never as an empty session: on a
  // 120k-row import every one of these timed out and the page claimed "No
  // staged resources for this session yet" (GDN-2247).
  const retryFor = (refetch: () => unknown) => () => {
    void refetch();
  };
  const loadFailures = [
    summaryError && { label: 'summary', retry: retryFor(refetchSummary) },
    // An open group states this failure itself, with its own Retry; two alerts
    // and two Retry buttons for one request is noise (GDN-3851).
    classifySummaryError &&
      !reviewOpen && {
        label: 'review verdicts',
        retry: retryFor(refetchClassifySummary),
      },
    conflictsError && { label: 'conflicts', retry: retryFor(refetchConflicts) },
    browseError && {
      label: 'staged records',
      retry: retryFor(refetchBrowse),
    },
    matchesError && {
      label: 'patient matches',
      retry: retryFor(refetchMatches),
    },
  ].filter((f): f is { label: string; retry: () => void } => Boolean(f));
  // An ambiguous patient must be resolved before the whole-session commit;
  // scoped row/cohort commits are gated server-side against their own
  // selection (GDN-2250). The match feed covers every staged patient.
  const unresolvedAmbiguous = selectedTypes.includes('patient')
    ? [...matchByKey.entries()]
        // Held ambiguous patients block nothing, for the same reason.
        .filter(([k, m]) => m.status === 'ambiguous' && !resolutions.has(k) && !m.internal_hold)
        .map(([k]) => k)
    : [];
  // Complete per-verdict patient cohorts for filter-scoped commits
  // (GDN-2251): from the fully-paged match + conflict feeds, never the browse
  // window. 'matched' patients auto-link; they commit with the whole type.
  const patientKeysByVerdict = useMemo(() => {
    const cohorts: Partial<Record<ImportVerdict, string[]>> = {
      new: [],
      ambiguous: [],
      conflict: [],
    };
    // Held rows (GDN-4065) are in no cohort: no commit writes them, so a
    // "commit N" count that included them would promise what it can't do.
    for (const [key, m] of matchByKey) {
      if (m.internal_hold) continue;
      if (m.status === 'new') cohorts.new?.push(key);
      else if (m.status === 'ambiguous') cohorts.ambiguous?.push(key);
    }
    for (const c of conflicts?.items ?? []) {
      if (c.resource_type === 'patient' && !c.internal_hold) cohorts.conflict?.push(c.source_key);
    }
    return cohorts;
  }, [matchByKey, conflicts]);

  // Commit board rows (GDN-2253): ingest counts + what still needs a human,
  // per type. Open decisions come from the fully-paged conflict feed plus,
  // for patients, unresolved ambiguous matches.
  const boardRows = resourceTypes.map(rt => {
    const c = countsByType[rt] ?? {};
    const openConflictsOfType = (conflicts?.items ?? []).filter(
      x => x.resource_type === rt && !decisions[x.import_resource_id] && !x.internal_hold,
    ).length;
    const openMatchesOfType = rt === 'patient' ? unresolvedAmbiguous.length : 0;
    return {
      resourceType: rt,
      staged: c.staged ?? 0,
      committed: c.committed ?? 0,
      skipped: c.skipped ?? 0,
      invalid: c.invalid ?? 0,
      openDecisions: openConflictsOfType + openMatchesOfType,
      unmappedProviders:
        rt === 'encounter'
          ? unmappedRequired.length
          : rt === 'provider'
            ? unmappedRequiredProviders.length
            : undefined,
    };
  });

  const totalOpenDecisions = boardRows.reduce((acc, r) => acc + r.openDecisions, 0);

  // Every conflicting row of a type being committed must carry a decision: the
  // backend blocks the run otherwise (no auto-escalation). Scope mirrors the
  // backend gate — by resource type, not the patient picker. Computed from the
  // fully-loaded conflict set (paged to completion), so it's complete regardless
  // of the browse window; conflictsPending (below) covers the load-in-flight gap.
  // A held conflict blocks nothing (GDN-4065): the server's gate skips held
  // rows, and the row's decisions are disabled while it is held, so counting
  // it would leave a gate the operator can only clear by releasing the hold.
  const unresolvedConflicts = (conflicts?.items ?? []).filter(
    c =>
      selectedTypes.includes(c.resource_type) &&
      !decisions[c.import_resource_id] &&
      !c.internal_hold,
  );
  // Until the conflict fetch resolves we can't see the conflicts yet, so a
  // premature commit would be rejected server-side. Gate while it's in flight.
  const conflictsPending = hasMatchedChanges && (conflictsLoading || !conflicts);

  // Gate while match results are still loading: until they resolve, matchByKey
  // is empty so unresolvedAmbiguous can't see ambiguous patients yet.
  const commitGated =
    providerGated ||
    matchesLoading ||
    unresolvedAmbiguous.length > 0 ||
    matchesIncomplete ||
    conflictsPending ||
    conflictsIncomplete ||
    unresolvedConflicts.length > 0;

  const isFullCommit = allSelected;
  const scopedStagedCount = selectedStagedCount;

  const [commitStagedResources] = useCommitStagedResourcesMutation();
  const [requestClassify, { isLoading: reclassifying }] = useRequestClassifyMutation();
  const handleReclassify = async () => {
    try {
      await requestClassify({ organizationId, sessionId }).unwrap();
      enqueueSnackbar('Re-classify started — verdicts refresh in about a minute.', {
        variant: 'info',
      });
    } catch {
      enqueueSnackbar('Failed to start re-classify.', { variant: 'error' });
    }
  };
  // Whether the operation being tracked committed the whole selection (the
  // header Commit button) or a patient-scoped slice (row/cohort commit). One
  // commit runs at a time (`committing` gates every entry point), so a single
  // flag is enough.
  const scopedCommitRef = useRef(false);
  // What the last commit left out because of holds (GDN-4065). Persistent,
  // unlike the snackbar, so the rows can be read; cleared when the next commit
  // starts, so it never describes a run other than the latest.
  const [commitHeld, setCommitHeld] = useState<CommitHeldResult | null>(null);
  useEffect(() => {
    if (committing) setCommitHeld(null);
  }, [committing]);
  const operationTracking = useImportOperationTracking({
    sessionId,
    onOperationComplete: operation => {
      setCommitting(false);
      if (!scopedCommitRef.current) {
        // After a full commit, reset so the next run defaults back to "all
        // staged": every type, no carried-over provider mapping or decisions.
        // resetProviderMapping also cancels any pending autosave so the stale
        // pre-reset map isn't written back after the reset.
        resetProviderMapping();
        setResolutions(new Map());
        setDecisions({});
      }
      // After a scoped (row/cohort) commit, every other in-progress decision
      // stays: the whole point is committing slices while the rest of the
      // queue is still being worked. Entries for the rows that just committed
      // are harmless leftovers — committed rows leave the staged queue and
      // the engine skips them idempotently.
      const held = operation.held;
      setCommitHeld(held && (held.count > 0 || held.dependencyCount > 0) ? held : null);
      enqueueSnackbar(
        commitCompleteMessage(
          {
            imported: operation.progress?.imported_count,
            failed: operation.progress?.failed_count,
            skipped: operation.progress?.skipped_count,
          },
          held,
        ),
        { variant: 'success' },
      );
      refresh();
    },
    onOperationError: operation => {
      setCommitting(false);
      enqueueSnackbar(`Commit failed: ${operation.error ?? 'Unknown error'}`, {
        variant: 'error',
      });
      refresh();
    },
  });

  const handleCommit = async () => {
    setConfirmOpen(false);
    setCommitting(true);
    scopedCommitRef.current = false;
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          // Send the explicit subset; all-selected == commit everything.
          resource_types: selectedTypes,
          // Source provider -> platform user; encounters land owned by the
          // mapped clinician. Unmapped required providers are gated before here.
          provider_mapping: providerMapping,
          // Operator match decisions; omit when none so auto-matching applies.
          patient_resolutions: resolutions.size > 0 ? Object.fromEntries(resolutions) : undefined,
          // Operator conflict decisions, keyed by import_resource_id; omit when
          // none. The commit button is gated until every in-scope conflict is
          // decided, so by here these cover them.
          conflict_resolutions: Object.keys(decisions).length > 0 ? decisions : undefined,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data?.message ??
        'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Progressive-trust commit (GDN-2250/2251): commit the given patients'
  // RECORDS only (children come later via type-scoped runs). The server
  // re-gates against exactly this selection, so conflicts outside it don't
  // block, and a premature commit of an undecided patient blocks cleanly.
  const commitPatients = async (patientKeys: string[]) => {
    setCommitting(true);
    scopedCommitRef.current = true;
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          resource_types: ['patient'],
          patient_keys: patientKeys,
          patient_resolutions: resolutions.size > 0 ? Object.fromEntries(resolutions) : undefined,
          conflict_resolutions: Object.keys(decisions).length > 0 ? decisions : undefined,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data?.message ??
        'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Type-wave commit (GDN-2253): one type, everything staged in it. The
  // board only enables this once the type's own gates and its parent wave
  // pass, and the server re-checks everything regardless.
  const commitTypes = async (resourceTypeToCommit: string, scopeToCommittedPatients = false) => {
    setCommitting(true);
    scopedCommitRef.current = true;
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          resource_types: [resourceTypeToCommit],
          provider_mapping: providerMapping,
          // Progressive dependents (GDN-2276): commit only the rows whose
          // patient is already committed; the rest stay staged for a later run.
          scope_to_committed_patients: scopeToCommittedPatients || undefined,
          patient_resolutions: resolutions.size > 0 ? Object.fromEntries(resolutions) : undefined,
          conflict_resolutions: Object.keys(decisions).length > 0 ? decisions : undefined,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data?.message ??
        'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Per-row dependency-aware commit (GDN-2376): commit exactly this one staged
  // row via `import_resource_ids`. The BE commits it if its ref deps are met,
  // else blocks it with a clear ref reason. Reuses the same mutation + tracking
  // + toast as the other commit callers; the whole-batch / per-type / cohort
  // paths are untouched. Carries `provider_mapping` like `commitTypes` does —
  // an encounter/note owned by a source provider blocks server-side unless the
  // operator's mapping rides along, even for a one-row commit. `import_resource_ids`
  // isn't in the generated CommitStagedResourcesRequest yet (BE in-flight), so
  // the request is cast — the same convention this file uses for other
  // not-yet-codegen'd fields.
  const commitRow = async (row: ReviewResourceRow) => {
    setCommitting(true);
    scopedCommitRef.current = true;
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          import_resource_ids: [row.id],
          provider_mapping: providerMapping,
          patient_resolutions: resolutions.size > 0 ? Object.fromEntries(resolutions) : undefined,
          conflict_resolutions: Object.keys(decisions).length > 0 ? decisions : undefined,
        } as CommitStagedResourcesRequest,
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data?.message ??
        'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Group-detail render states (GDN-3851). Whatever the classify summary did,
  // an open group always draws something: the verdict browser, an explicit
  // reason, or the staged rows without verdicts — never an empty body.
  // A failed REFETCH keeps the last good data (RTK Query leaves `data` in place
  // on rejection), so the error flag must not decide what renders — only what
  // is drawn ON TOP of it. Gate on whether there is anything to draw instead,
  // or a blink on any refetch reproduces the empty body this fixes (GDN-3851).
  // Verdict presence for the OPEN GROUP, not the session. The summary is
  // deliberately session-wide (it gates the conflict fetch across every
  // status), but classify lands per type: mid-migration, patient can carry
  // verdicts while encounter carries none. Reading the session flag here would
  // send a type with no verdicts of its own down the browser branch, where
  // every unclassified row is filtered out and the fallback list is suppressed
  // — an empty browser and no rows, which is the bug this fixes arriving by
  // another route (GDN-3851 review).
  const groupVerdictCount = useMemo(() => {
    if (!classifySummary) return 0;
    if (browseType === 'all') return classifySummary.total_records ?? 0;
    const forType = classifySummary.by_type?.find(t => t.resource_type === browseType);
    return Object.values(forType?.counts ?? {}).reduce((acc, n) => acc + (n ?? 0), 0);
  }, [classifySummary, browseType]);
  const groupHasVerdicts = groupVerdictCount > 0;
  // `browseFetching` keeps the fallback browser mounted across an argument
  // change. Without it, a group with staged rows but no verdicts loses its
  // search box, its focus, its pager and its expanded set on every page turn
  // and every keystroke, which is the blink GDN-3851 fixed.
  const reviewBrowserVisible =
    !classifySummaryLoading && (groupHasVerdicts || reviewRows.length > 0 || browseFetching);
  const stagedListVisible = !classifySummaryLoading && !groupHasVerdicts;
  const classifySummaryReason = queryErrorReason(classifySummaryErrorDetail);
  // Lane counts for the derived (no-verdict) browser. Taken per verdict from
  // the rows themselves: the page can carry unprocessable rows too, so the row
  // COUNT is not the ambiguous count.
  const derivedTotals = useMemo(() => {
    const counts: VerdictCounts = { ...EMPTY_VERDICT_COUNTS };
    for (const r of reviewRows) {
      if (r.verdict) counts[r.verdict] += 1;
    }
    return counts;
  }, [reviewRows]);
  const ambiguousOnPage = derivedTotals.ambiguous;
  // What the WHOLE session still needs decided — from the fully-paged match
  // feed, the same source the board counts. The browser only ever shows the
  // loaded page, so say so rather than implying the page is the total.
  const sessionAmbiguousCount = useMemo(
    () => [...matchByKey.values()].filter(m => m.status === 'ambiguous').length,
    [matchByKey],
  );
  // The derived lane is patients only, and every surface here is scoped to the
  // open type. Somewhere else's ambiguous matches must not be described as
  // decidable by paging through THESE rows (GDN-3851 review).
  const patientRowsInView = browseType === 'patient' || browseType === 'all';

  // The plain staged list (type + status filters, expandable rows, paging).
  // Rendered on the Overview when there are no verdicts to lane, and under an
  // open group's fallback states with the type locked to that group.
  const renderStagedList = ({ typeLocked }: { typeLocked: boolean }) => (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        {!typeLocked && (
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Resource type</InputLabel>
            <Select
              label="Resource type"
              value={listResourceType}
              onChange={e => {
                setResourceType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {resourceTypes.map(rt => (
                <MenuItem key={rt} value={rt}>
                  {rt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={e => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isFetching && <CircularProgress size={18} />}
      </Box>

      {listLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : isFetching && items.length === 0 ? (
        <Box role="status" sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
          <Box component="span" className="sr-only">
            Loading the selected rows
          </Box>
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No resources match the current filters.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Resource type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Committed ID</TableCell>
              <TableCell>Source key</TableCell>
              <TableCell align="right">Errors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => {
              const isOpen = expanded === item.import_resource_id;
              const errs = item.validation_errors ?? [];
              const commitErrs = item.commit_errors ?? [];
              return (
                <Fragment key={item.import_resource_id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label="Toggle details"
                        onClick={() => setExpanded(isOpen ? null : item.import_resource_id)}
                      >
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{item.resource_type}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.status}
                        color={STATUS_COLOR[item.status] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {item.committed_resource_id ? (
                        <Link
                          to={`/admin-v2/resources/${item.resource_type}/${item.committed_resource_id}?organization_id=${organizationId}`}
                        >
                          {item.committed_resource_id}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                      }}
                    >
                      {item.source_key}
                    </TableCell>
                    <TableCell align="right">
                      {errs.length > 0 || commitErrs.length > 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'flex-end',
                          }}
                        >
                          {errs.length > 0 && (
                            <Chip size="small" color="error" label={`${errs.length} validation`} />
                          )}
                          {commitErrs.length > 0 && (
                            <Chip
                              size="small"
                              color="warning"
                              label={`${commitErrs.length} commit`}
                            />
                          )}
                        </Box>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, border: isOpen ? undefined : 0 }}>
                      <Collapse in={isOpen} unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          {errs.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="error">
                                Validation errors
                              </Typography>
                              <pre
                                style={{
                                  margin: 0,
                                  fontSize: 12,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {JSON.stringify(errs, null, 2)}
                              </pre>
                            </Box>
                          )}
                          {commitErrs.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="warning.main">
                                Commit errors
                              </Typography>
                              <pre
                                style={{
                                  margin: 0,
                                  fontSize: 12,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {JSON.stringify(commitErrs, null, 2)}
                              </pre>
                            </Box>
                          )}
                          <Typography variant="subtitle2">Data</Typography>
                          <pre
                            style={{
                              margin: 0,
                              fontSize: 12,
                              whiteSpace: 'pre-wrap',
                              maxHeight: 320,
                              overflow: 'auto',
                            }}
                          >
                            {JSON.stringify(item.data ?? {}, null, 2)}
                          </pre>
                          {item.refs && Object.keys(item.refs).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2">Refs</Typography>
                              <pre style={{ margin: 0, fontSize: 12 }}>
                                {JSON.stringify(item.refs, null, 2)}
                              </pre>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, p) => setPage(p)}
            // Held open on the last known count so it does not vanish under the
            // cursor, but not clickable while that count is the previous
            // selection's: page 7 may not exist in the one being loaded.
            disabled={isFetching}
            size="small"
          />
        </Box>
      )}
    </>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Staged Resources"
        subheader={
          summary
            ? `Session ${summary.status} · ${total} resource(s)` +
              (summary.s3_prefix ? ` · ${summary.s3_prefix}` : '')
            : undefined
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showCommit && (
              <Tooltip
                title={
                  matchesLoading || conflictsPending
                    ? 'Loading review results…'
                    : matchesIncomplete
                      ? 'Too many patients to load match results at once — commit is disabled to avoid missing an ambiguous match'
                      : conflictsIncomplete
                        ? 'Too many conflicts to load at once — commit is disabled to avoid missing a required decision'
                        : unresolvedAmbiguous.length > 0
                          ? `Resolve ${unresolvedAmbiguous.length} ambiguous patient match(es) before committing`
                          : unresolvedConflicts.length > 0
                            ? `Resolve ${unresolvedConflicts.length} conflict(s) before committing`
                            : providerGated
                              ? 'Map all providers with encounters before committing'
                              : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={committing ? <CircularProgress size={16} /> : <Publish />}
                    disabled={committing || scopedStagedCount === 0 || commitGated}
                    onClick={() => setConfirmOpen(true)}
                  >
                    {committing
                      ? 'Committing…'
                      : isFullCommit
                        ? `Commit staged (${scopedStagedCount})`
                        : `Commit selected (${scopedStagedCount})`}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Re-run the classify pass — use after a matching/classify change so verdicts reflect current behavior. Async; refresh after a minute.">
              <span>
                <Button
                  variant="outlined"
                  disabled={reclassifying}
                  onClick={() => void handleReclassify()}
                >
                  Re-run classify
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Batch already converted — adding files is locked. Start a new batch instead.">
              <span>
                <Button variant="outlined" disabled>
                  Add files
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={refresh} aria-label="Refresh staged resources">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {loadFailures.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => loadFailures.forEach(f => f.retry())}
              >
                Retry
              </Button>
            }
          >
            Couldn&apos;t load {loadFailures.map(f => f.label).join(', ')} for this session — the
            request failed or timed out. The counts and lists below may be missing or incomplete;
            the staged data itself is not affected.
          </Alert>
        )}
        {committing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Commit running in the background — staged resources are being written to the live
            tables. This view refreshes when it finishes.
          </Alert>
        )}
        {commitHeld && (
          <CommitHeldAlert held={commitHeld} onDismiss={() => setCommitHeld(null)} />
        )}
        {openType === PROVIDERS_SURFACE && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Button size="small" onClick={() => setOpenSurface(null)}>
                ← Overview
              </Button>
              <Typography variant="h6">Source providers</Typography>
              <Typography variant="body2" color="text.secondary">
                {unmappedRequiredProviders.length > 0
                  ? `${unmappedRequiredProviders.length} to map`
                  : 'all mapped'}
              </Typography>
              <AutosaveStatus
                status={mappingSaveStatus}
                isDirty={mappingDirty}
                lastSavedAt={mappingSavedAt}
                onRetry={retryProviderMappingSave}
                verbose
              />
            </Box>
            <ProviderMappingPanel
              organizationId={organizationId}
              sessionId={sessionId}
              providers={providers}
              value={providerMapping}
              onChange={onProviderMappingChange}
              encounterSelected={encounterSelected}
              suggestedMapping={suggestedMapping}
              // The other lens on the same record (GDN-2412): jump to this
              // provider's staged row in the review browser, focused by its
              // source key.
              //
              // Offered only where a provider group exists to jump into. When
              // the converter emitted no provider envelopes this surface is
              // reached from a synthetic row (GDN-2256) and there is no staged
              // row to focus, so the jump would name a group the session does
              // not have and be bounced straight back to the Overview with a
              // search term the operator could no longer see. The panel hides
              // the control when the handler is absent, so withholding it
              // leaves nothing dead on screen.
              onInspectProvider={
                // `sessionTypes`, not `resourceTypes`: the latter comes from
                // `data`, which is the last summary for ANY session, so during
                // a session change it would still say a provider group exists
                // and re-open the very veto this gate prevents. Same discipline
                // as the two URL guards above.
                Object.hasOwn(sessionTypes ?? {}, 'provider')
                  ? key => {
                      setBrowseSearch(key);
                      openReview('provider');
                    }
                  : undefined
              }
            />
          </Box>
        )}
        {reviewOpen && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Button size="small" onClick={closeReview}>
                ← Overview
              </Button>
              <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                {browseType === 'all' ? 'All types' : browseType.replaceAll('_', ' ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(browseType === 'all'
                  ? Object.values(countsByType).reduce((acc, c) => acc + (c.staged ?? 0), 0)
                  : (countsByType[browseType]?.staged ?? 0)
                ).toLocaleString()}{' '}
                staged
              </Typography>
            </Box>
            {classifySummaryError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void refetchClassifySummary()}
                  >
                    Retry
                  </Button>
                }
              >
                Review verdicts for this session couldn&apos;t be loaded
                {classifySummaryReason ? ` (${classifySummaryReason})` : ''} —{' '}
                {groupHasVerdicts
                  ? 'the lanes below are the ones that last loaded and may be out of date.'
                  : 'the new / update / conflict lanes need them. The staged rows are listed below without verdicts.'}
              </Alert>
            )}
            {classifySummaryLoading ? (
              <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading review verdicts…
                </Typography>
              </Box>
            ) : classifySummaryError ? null : !groupHasVerdicts ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {hasClassifyVerdicts
                  ? 'No review verdicts for this resource type yet — the classify pass hasn’t recorded a verdict for any staged row of this type, so the new / update / conflict lanes aren’t available here. '
                  : 'No review verdicts for this session yet — the classify pass hasn’t recorded a verdict for any staged row, so the new / update / conflict lanes aren’t available. '}
                Use Re-run classify above, then Refresh.{' '}
                {sessionAmbiguousCount > 0 &&
                  (patientRowsInView
                    ? `${sessionAmbiguousCount.toLocaleString()} ambiguous patient ${
                        sessionAmbiguousCount === 1 ? 'match' : 'matches'
                      } from the matching pass can be decided${
                        sessionAmbiguousCount > ambiguousOnPage
                          ? ' — they appear as you page through the staged rows'
                          : ''
                      }. `
                    : `${sessionAmbiguousCount.toLocaleString()} ambiguous patient ${
                        sessionAmbiguousCount === 1 ? 'match' : 'matches'
                      } from the matching pass can be decided in the patient group. `)}
                All staged rows of this type are listed below.
              </Alert>
            ) : (
              <>
                {(conflictsIncomplete || matchesIncomplete) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This import is unusually large and some conflict or patient-match data
                    couldn&apos;t be fully loaded for review. Committing is disabled to avoid
                    missing a required decision — contact support.
                  </Alert>
                )}
                {!conflictsIncomplete && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Browse, filters, and search cover the whole import.{' '}
                    {totalConflictCount > 0
                      ? `All ${totalConflictCount.toLocaleString()} conflict(s) are loaded below and must be resolved before committing. `
                      : 'This import has no conflicts to review. '}
                    {autoLinkCount > 0 &&
                      `${autoLinkCount.toLocaleString()} patient(s) will link to existing records — verify the matches below before committing.`}
                  </Alert>
                )}
              </>
            )}
            {reviewBrowserVisible && (
              <ImportReviewBrowser
                sourceLabel={
                  groupHasVerdicts ? (classifySummary?.source_label ?? undefined) : undefined
                }
                totalRecords={
                  groupHasVerdicts ? scopedTotalRecords || reviewRows.length : reviewRows.length
                }
                totals={groupHasVerdicts ? browserTotals : derivedTotals}
                rows={reviewRows}
                decisions={decisions}
                onDecisionChange={handleDecisionChange}
                disabledRowIds={resolvingIds}
                matchResolutions={browserMatchResolutions}
                onMatchResolutionChange={handleMatchResolution}
                serverBrowse={{
                  typeFilter: browseType,
                  onTypeFilterChange: setBrowseType,
                  statusFilter: browseStatus,
                  onStatusFilterChange: setBrowseStatus,
                  // Server-driven verdict chips need verdicts to filter on. With
                  // none, every classification is NULL, so a chip would fetch an
                  // empty page and unmount the browser that carries the chip —
                  // one click, no way back. Hand the browser no verdict wiring
                  // there and it slices the loaded rows itself (GDN-3851).
                  ...(groupHasVerdicts
                    ? {
                        verdictFilter: browseVerdict,
                        onVerdictFilterChange: setBrowseVerdict,
                      }
                    : {}),
                  fetching: browseFetching,
                  // The chips' numbers come from the status-scoped summary, so
                  // they are unknown until it answers for the current scope.
                  countsPending: !statusScopedSummary,
                  search: browseSearch,
                  onSearchChange: setBrowseSearch,
                  page: browsePage,
                  onPageChange: setBrowsePage,
                  perPage: BROWSE_PAGE_SIZE,
                  totalRows: browseTotalRows,
                  resourceTypes: Object.keys(summary?.counts_by_resource_type ?? {}).sort(),
                }}
                selection={{
                  selectedIds,
                  onToggle: handleToggleSelect,
                  onClear: handleClearSelection,
                  selectedTotal,
                  byActor: selectionByActor,
                  filterActive: browseSelected,
                  onFilterChange: setBrowseSelected,
                }}
                orgReview={{
                  flaggedIds: orgReviewIds,
                  flaggedBy: orgReviewByById,
                  onToggle: handleToggleOrgReview,
                  total: orgReviewTotal,
                  byActor: orgReviewByActor,
                  filterMode: orgReviewFilter,
                  onFilterModeChange: setOrgReviewFilter,
                  notFlaggedTotal,
                }}
                internalHold={internalHold}
                commit={
                  showCommit
                    ? {
                        onCommitPatients: keys => void commitPatients(keys),
                        inFlight: committing,
                        patientKeysByVerdict,
                      }
                    : undefined
                }
                onViewDocument={id => setDocViewId(id)}
                // Committed ref target → its Resource Explorer page (GDN-2376),
                // same URL shape as the committed_resource_id link in the staged
                // table below.
                resourceHref={(type, id) =>
                  `/admin-v2/resources/${type}/${id}?organization_id=${organizationId}`
                }
                // Staged ref target → drill into that row's review by driving the
                // server-browse controls this host owns: scope to the target's
                // type and search its source key. openReview sets type + page +
                // surface; setBrowseSearch focuses the row.
                onOpenStagedRef={t => {
                  setBrowseSearch(t.source_key);
                  openReview(t.resource_type);
                }}
                // Dependency-aware per-row commit (GDN-2376), enabled only when the
                // host offers commit. Passed for every row; ImportReviewBrowser
                // renders the button only for NON-patient rows (patients keep their
                // progressive-trust Commit above), so no row shows two buttons.
                onCommitRow={showCommit ? row => void commitRow(row) : undefined}
                // Up-front per-row blocker the ref chips don't cover (GDN-2376): a
                // row owned by a still-unmapped source provider blocks server-side
                // (provider ownership is a mapping gate, distinct from the provider
                // *resource* dependency the chip shows). Name it so the operator
                // maps the provider first instead of clicking into an opaque
                // failure. Reads the freshest local providerMapping.
                commitRowDisabledReason={row => {
                  const providerRef = row.refTargets?.find(
                    t => t.role === 'provider' || t.role === 'supervisor',
                  );
                  if (providerRef && !providerMapping[providerRef.source_key]) {
                    return 'Map this record’s provider before committing it';
                  }
                  return undefined;
                }}
                // Provider-mapping gate (GDN-2412): unmapped provider rows offer
                // "Map provider" (→ the Source providers surface) instead of a
                // dead-end Commit, and browsing providers shows the unmapped
                // banner. Counts read the live local mapping — the same map the
                // commit input carries.
                providerMappingGate={{
                  unmappedCount: providers.filter(p => !providerMapping[p.provider_key]).length,
                  totalCount: providers.length,
                  onOpenMapping: () => setOpenSurface(PROVIDERS_SURFACE),
                }}
                showCommitBar={false}
                provenance={
                  hasProvenance ? (
                    <Box sx={{ fontSize: 13 }}>
                      {converterSha256 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            mb: 0.5,
                          }}
                        >
                          convert.py SHA-256 · {converterSha256}
                        </Typography>
                      )}
                      {artifactKeys.map(key => (
                        <Typography
                          key={key}
                          variant="caption"
                          onClick={() => setViewerKey(key)}
                          sx={{
                            display: 'block',
                            color: 'primary.main',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {artifactName(key, artifactsPrefix)}
                        </Typography>
                      ))}
                    </Box>
                  ) : undefined
                }
              />
            )}
            {stagedListVisible && renderStagedList({ typeLocked: true })}
          </Box>
        )}
        {openType === null && (
          <>
            {showCommit && hasClassifyVerdicts && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {totalOpenDecisions > 0 || unmappedRequiredProviders.length > 0
                  ? `${totalOpenDecisions.toLocaleString()} decision${
                      totalOpenDecisions === 1 ? '' : 's'
                    } and ${unmappedRequiredProviders.length} provider mapping${
                      unmappedRequiredProviders.length === 1 ? '' : 's'
                    } stand between this import and its next commit. Click a row to review; commit types top to bottom.`
                  : 'Nothing needs a decision — commit types top to bottom, or everything at once.'}
              </Alert>
            )}
            {summaryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : resourceTypes.length === 0 ? (
              summaryError ? (
                <Typography variant="body2" color="error">
                  The staged-resource summary couldn&apos;t be loaded — use Retry above.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No staged resources for this session yet.
                </Typography>
              )
            ) : showCommit ? (
              <CommitBoard
                rows={boardRows}
                providers={
                  // With staged provider envelopes the provider TYPE row carries
                  // the mapping task; the synthetic row is the fallback for
                  // batches whose converter emitted none (GDN-2256).
                  providers.length > 0 && !resourceTypes.includes('provider')
                    ? {
                        total: providers.length,
                        unmapped: unmappedRequiredProviders.length,
                      }
                    : undefined
                }
                onOpenProviders={() => setOpenSurface(PROVIDERS_SURFACE)}
                waitsFor={
                  (summary as { waits_for?: Record<string, string[]> } | undefined)?.waits_for ?? {}
                }
                commitReadyByType={
                  (summary as { commit_ready_by_type?: Record<string, number> } | undefined)
                    ?.commit_ready_by_type ?? {}
                }
                onCommitType={rt => void commitTypes(rt)}
                onCommitReady={rt => void commitTypes(rt, true)}
                onOpenType={rt =>
                  rt === 'provider' ? setOpenSurface(PROVIDERS_SURFACE) : openReview(rt)
                }
                commitInFlight={committing}
              />
            ) : (
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Resource type</TableCell>
                    {STATUSES.map(s => (
                      <TableCell key={s} align="right">
                        {s}
                      </TableCell>
                    ))}
                    <TableCell align="right">total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resourceTypes.map(rt => {
                    const row = countsByType[rt] ?? {};
                    const rowTotal = Object.values(row).reduce((a, b) => a + b, 0);
                    return (
                      <TableRow
                        key={rt}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setResourceType(rt);
                          setPage(1);
                        }}
                      >
                        <TableCell>{rt}</TableCell>
                        {STATUSES.map(s => (
                          <TableCell key={s} align="right">
                            {row[s] ?? 0}
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          <strong>{rowTotal}</strong>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {hasProvenance && (
              <Box sx={{ mb: 1 }}>
                <Button size="small" onClick={() => setProvenanceOpen(v => !v)}>
                  {provenanceOpen ? 'Hide' : 'Show'} provenance & artifacts
                </Button>
              </Box>
            )}
            {hasProvenance && (
              <Collapse in={provenanceOpen} unmountOnExit>
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Converter provenance
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}
                  >
                    How this batch was converted, captured when it was staged.
                  </Typography>
                  {converterSha256 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: artifactsPrefix || artifactKeys.length ? 1 : 0,
                      }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 120 }}>
                        convert.py SHA-256
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          wordBreak: 'break-all',
                          flex: 1,
                        }}
                      >
                        {converterSha256}
                      </Typography>
                      <Tooltip title="Copy SHA-256">
                        <IconButton
                          size="small"
                          aria-label="Copy converter SHA-256"
                          onClick={() => copy(converterSha256, 'SHA-256')}
                        >
                          <ContentCopy fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {artifactsPrefix && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: artifactKeys.length ? 1 : 0,
                      }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 120 }}>
                        Artifacts prefix
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          wordBreak: 'break-all',
                          flex: 1,
                        }}
                      >
                        {artifactsPrefix}
                      </Typography>
                      <Tooltip title="Copy prefix">
                        <IconButton
                          size="small"
                          aria-label="Copy artifacts prefix"
                          onClick={() => copy(artifactsPrefix, 'Prefix')}
                        >
                          <ContentCopy fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {artifactKeys.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Artifacts
                      </Typography>
                      {artifactKeys.map(key => (
                        <Box
                          key={key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.25,
                          }}
                        >
                          <Typography
                            variant="body2"
                            onClick={() => setViewerKey(key)}
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: 12,
                              wordBreak: 'break-all',
                              flex: 1,
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {artifactName(key, artifactsPrefix)}
                          </Typography>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              aria-label={`View artifact ${key}`}
                              onClick={() => setViewerKey(key)}
                            >
                              <Visibility fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              aria-label={`Download artifact ${key}`}
                              onClick={() => void openArtifact(key)}
                            >
                              <Download fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            )}

            {!hasClassifyVerdicts && renderStagedList({ typeLocked: false })}
          </>
        )}
      </CardContent>

      <SourceDocumentModal
        open={docViewId !== null}
        onClose={() => setDocViewId(null)}
        content={docContent}
        loading={docLoading}
        error={docError}
        url={docUrl?.url}
        urlLoading={docUrlLoading}
        urlError={docUrlError}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{isFullCommit ? 'Commit staged resources?' : 'Commit selected?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This writes {scopedStagedCount} staged resource
            {scopedStagedCount === 1 ? '' : 's'}
            {allSelected ? '' : ` (${selectedTypes.join(', ')})`} into the live tables for this
            organization. Rows that fail are marked with commit errors and the run continues;
            already-committed rows are skipped, so re-running is safe.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleCommit()}>
            Commit
          </Button>
        </DialogActions>
      </Dialog>

      <ArtifactViewerModal
        open={viewerKey !== null}
        onClose={() => setViewerKey(null)}
        organizationId={organizationId}
        sessionId={sessionId}
        artifactKey={viewerKey}
        displayName={viewerKey ? artifactName(viewerKey, artifactsPrefix) : ''}
        onDownload={key => void openArtifact(key)}
      />
    </Card>
  );
}
